import toast from 'react-hot-toast';
import { 
  FaCheckCircle, 
  FaExclamationCircle, 
  FaInfoCircle, 
  FaExclamationTriangle,
  FaClock
} from 'react-icons/fa';

/**
 * Centralized Notification Utility
 * 
 * Provides consistent success and error messages with appropriate colors and icons
 * throughout the application.
 */

// Custom toast styles
const baseStyle = {
  borderRadius: '12px',
  padding: '16px',
  fontSize: '14px',
  fontWeight: '500',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  maxWidth: '500px',
};

/**
 * Display a success notification
 * @param {string} message - The success message to display
 * @param {object} options - Additional toast options
 */
export const notifySuccess = (message, options = {}) => {
  return toast.success(message, {
    duration: 4000,
    icon: <FaCheckCircle className="text-green-500 text-xl" />,
    style: {
      ...baseStyle,
      background: '#ffffff',
      color: '#1f2937',
      border: '2px solid #10b981',
    },
    ...options,
  });
};

/**
 * Display an error notification
 * @param {string} message - The error message to display
 * @param {object} options - Additional toast options
 */
export const notifyError = (message, options = {}) => {
  return toast.error(message, {
    duration: 5000,
    icon: <FaExclamationCircle className="text-red-500 text-xl" />,
    style: {
      ...baseStyle,
      background: '#ffffff',
      color: '#1f2937',
      border: '2px solid #ef4444',
    },
    ...options,
  });
};

/**
 * Display a warning notification
 * @param {string} message - The warning message to display
 * @param {object} options - Additional toast options
 */
export const notifyWarning = (message, options = {}) => {
  return toast(message, {
    duration: 4500,
    icon: <FaExclamationTriangle className="text-yellow-500 text-xl" />,
    style: {
      ...baseStyle,
      background: '#ffffff',
      color: '#1f2937',
      border: '2px solid #f59e0b',
    },
    ...options,
  });
};

/**
 * Display an info notification
 * @param {string} message - The info message to display
 * @param {object} options - Additional toast options
 */
export const notifyInfo = (message, options = {}) => {
  return toast(message, {
    duration: 4000,
    icon: <FaInfoCircle className="text-blue-500 text-xl" />,
    style: {
      ...baseStyle,
      background: '#ffffff',
      color: '#1f2937',
      border: '2px solid #3b82f6',
    },
    ...options,
  });
};

/**
 * Display a pending/awaiting notification
 * @param {string} message - The pending message to display
 * @param {object} options - Additional toast options
 */
export const notifyPending = (message, options = {}) => {
  return toast(message, {
    duration: 5000,
    icon: <FaClock className="text-orange-500 text-xl" />,
    style: {
      ...baseStyle,
      background: '#ffffff',
      color: '#1f2937',
      border: '2px solid #f97316',
    },
    ...options,
  });
};

// Predefined notification messages for common scenarios
export const NotificationMessages = {
  // Review related
  REVIEW_SUBMITTED: 'Thank you! Your review has been submitted and is pending approval.',
  REVIEW_SUBMIT_ERROR: 'Failed to submit review. Please try again.',
  REVIEW_APPROVED: 'Review approved successfully!',
  REVIEW_DELETED: 'Review deleted successfully.',
  REVIEW_DELETE_ERROR: 'Failed to delete review.',
  
  // Form validation
  VALIDATION_NAME_REQUIRED: 'Please enter your name.',
  VALIDATION_NAME_TOO_SHORT: 'Name must be at least 2 characters long.',
  VALIDATION_NAME_TOO_LONG: 'Name must not exceed 50 characters.',
  VALIDATION_EMAIL_REQUIRED: 'Please enter your email address.',
  VALIDATION_EMAIL_INVALID: 'Please enter a valid email address.',
  VALIDATION_RATING_REQUIRED: 'Please select a rating.',
  VALIDATION_COMMENT_REQUIRED: 'Please write a comment.',
  VALIDATION_COMMENT_TOO_SHORT: 'Comment must be at least 10 characters long.',
  VALIDATION_MESSAGE_REQUIRED: 'Please enter a message.',
  VALIDATION_SUBJECT_REQUIRED: 'Please select a subject.',
  
  // Contact form
  CONTACT_SENT: 'Message sent successfully! We\'ll get back to you within 24 hours.',
  CONTACT_ERROR: 'Failed to send message. Please try again later.',
  CONTACT_MARKED_READ: 'Message marked as read.',
  CONTACT_DELETED: 'Message deleted successfully.',
  CONTACT_DELETE_ERROR: 'Failed to delete message.',
  
  // Authentication
  LOGIN_SUCCESS: 'Login successful! Welcome back.',
  LOGIN_ERROR: 'Invalid credentials. Please try again.',
  LOGOUT_SUCCESS: 'Logged out successfully.',
  SESSION_EXPIRED: 'Your session has expired. Please login again.',
  
  // Generic
  LOADING_ERROR: 'Failed to load data. Please refresh the page.',
  NETWORK_ERROR: 'Network error. Please check your internet connection.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
};

const notifications = {
  success: notifySuccess,
  error: notifyError,
  warning: notifyWarning,
  info: notifyInfo,
  pending: notifyPending,
  messages: NotificationMessages,
};

export default notifications;
